import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  Divider,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Check as CheckIcon,
  Assignment as AssignmentIcon,
  NewReleases as NewReleasesIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface AnefSyncModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  onSyncSuccess: () => void;
}

const AnefSyncModal: React.FC<AnefSyncModalProps> = ({
  open,
  onClose,
  clientId,
  onSyncSuccess,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncData, setSyncData] = useState<any>(null);
  const [selectedDemarches, setSelectedDemarches] = useState<number[]>([]);
  const [importing, setImporting] = useState(false);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [checkingCredentials, setCheckingCredentials] = useState(true);

  // Vérifier si des identifiants ANEF sont enregistrés
  useEffect(() => {
    const checkStoredCredentials = async () => {
      if (!open || !clientId) return;

      setCheckingCredentials(true);
      try {
        const response = await api.get(`/clients/${clientId}/password-management`);
        if (response.data.success) {
          const anefPassword = response.data.data.passwords?.find(
            (pwd: any) => pwd.label === 'ANEF'
          );
          setHasStoredCredentials(!!anefPassword);

          // Si des identifiants existent, lancer automatiquement la synchronisation
          if (anefPassword) {
            handleSyncWithStoredCredentials();
          }
        }
      } catch (err) {
        console.error('Erreur vérification credentials:', err);
        setHasStoredCredentials(false);
      } finally {
        setCheckingCredentials(false);
      }
    };

    checkStoredCredentials();
  }, [open, clientId]);

  // Synchroniser avec les identifiants enregistrés
  const handleSyncWithStoredCredentials = async () => {
    setLoading(true);
    setError('');
    setSyncData(null);

    try {
      // Le backend utilisera automatiquement les identifiants enregistrés
      const response = await api.post(`/clients/${clientId}/anef/sync`, {});

      if (response.data.success) {
        setSyncData(response.data.data);
        const newCount = response.data.data.new_demarches?.length || 0;
        setSelectedDemarches(Array.from({ length: newCount }, (_, i) => i));

        if (response.data.data.updated > 0) {
          onSyncSuccess();
        }
      } else {
        setError(response.data.message || 'Erreur lors de la synchronisation');
      }
    } catch (err: any) {
      console.error('Erreur sync ANEF:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors de la connexion au compte ANEF.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!username || !password) {
      setError('Veuillez saisir les identifiants ANEF');
      return;
    }

    setLoading(true);
    setError('');
    setSyncData(null);

    try {
      const response = await api.post(`/clients/${clientId}/anef/sync`, {
        username,
        password,
      });

      if (response.data.success) {
        setSyncData(response.data.data);
        // Sélectionner automatiquement toutes les nouvelles démarches
        const newCount = response.data.data.new_demarches?.length || 0;
        setSelectedDemarches(Array.from({ length: newCount }, (_, i) => i));

        // Si des démarches ont été mises à jour, rafraîchir les données
        if (response.data.data.updated > 0) {
          onSyncSuccess();
        }
      } else {
        setError(response.data.message || 'Erreur lors de la synchronisation');
      }
    } catch (err: any) {
      console.error('Erreur sync ANEF:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors de la connexion au compte ANEF. Vérifiez les identifiants.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!syncData?.new_demarches || selectedDemarches.length === 0) {
      setError('Aucune démarche sélectionnée');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const demarchesToImport = syncData.new_demarches.filter(
        (_: any, index: number) => selectedDemarches.includes(index)
      );

      const response = await api.post('/demarches/bulk', {
        client_id: clientId,
        demarches: demarchesToImport,
      });

      if (response.data.success) {
        onSyncSuccess();
        handleClose();
      }
    } catch (err: any) {
      console.error('Erreur import démarches:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'import des démarches');
    } finally {
      setImporting(false);
    }
  };

  const handleToggleDemarche = (index: number) => {
    setSelectedDemarches(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleToggleAll = () => {
    const newDemarches = syncData?.new_demarches || [];
    if (selectedDemarches.length === newDemarches.length) {
      setSelectedDemarches([]);
    } else {
      setSelectedDemarches(newDemarches.map((_: any, index: number) => index));
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setError('');
    setSyncData(null);
    setSelectedDemarches([]);
    setHasStoredCredentials(false);
    setCheckingCredentials(true);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon />
          <Typography variant="h6">Synchroniser avec ANEF</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {checkingCredentials && !syncData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Vérification des identifiants enregistrés...
            </Typography>
          </Box>
        )}

        {!checkingCredentials && !syncData && !hasStoredCredentials && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Entrez les identifiants ANEF du client pour vérifier s'il y a de nouvelles démarches.
              Ces identifiants seront enregistrés pour les prochaines synchronisations.
            </Alert>

            <TextField
              fullWidth
              label="Email / Nom d'utilisateur ANEF"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              autoFocus
            />

            <TextField
              fullWidth
              type="password"
              label="Mot de passe ANEF"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSync();
                }
              }}
            />
          </Box>
        )}

        {loading && !syncData && !checkingCredentials && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Synchronisation en cours...
            </Typography>
          </Box>
        )}

        {syncData && (
          <Box>
            {/* Résumé de la synchronisation */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Résumé de la synchronisation
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${syncData.total_anef} démarche(s) sur ANEF`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${syncData.existing} déjà importée(s)`}
                  color="info"
                  variant="outlined"
                />
                {syncData.updated > 0 && (
                  <Chip
                    label={`${syncData.updated} mise(s) à jour`}
                    color="success"
                    icon={<CheckIcon />}
                  />
                )}
                <Chip
                  label={`${syncData.new} nouvelle(s)`}
                  color="warning"
                  icon={<NewReleasesIcon />}
                />
              </Box>
            </Box>

            {syncData.updated > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {syncData.updated} démarche{syncData.updated > 1 ? 's' : ''} existante{syncData.updated > 1 ? 's ont' : ' a'} été mise{syncData.updated > 1 ? 's' : ''} à jour avec les dernières données ANEF (notifications, titres, etc.)
              </Alert>
            )}

            {syncData.new === 0 ? (
              <Alert severity="success" icon={<CheckIcon />}>
                {syncData.updated > 0
                  ? 'Synchronisation terminée. Toutes les démarches sont à jour !'
                  : 'Aucune nouvelle démarche. Toutes les démarches ANEF sont déjà synchronisées !'}
              </Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <AssignmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Nouvelles démarches ({syncData.new})
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleToggleAll}
                    sx={{ textTransform: 'none' }}
                  >
                    {selectedDemarches.length === syncData.new_demarches.length
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  Sélectionnez les nouvelles démarches que vous souhaitez importer.
                </Alert>

                <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                  {syncData.new_demarches.map((demarche: any, index: number) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleToggleDemarche(index)}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        borderBottom: index < syncData.new_demarches.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedDemarches.includes(index)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {demarche.titre}
                            </Typography>
                            <Chip
                              label={demarche.statut}
                              size="small"
                              color={
                                demarche.statut === 'en_cours' ? 'primary' :
                                demarche.statut === 'termine' ? 'success' :
                                demarche.statut === 'en_attente' ? 'warning' : 'default'
                              }
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {demarche.necessite_intervention && (
                              <Chip
                                label="Intervention requise"
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {demarche.description && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {demarche.description}
                              </Typography>
                            )}
                            {demarche.date_depot && (
                              <Typography variant="caption" color="text.secondary">
                                Déposé le: {new Date(demarche.date_depot).toLocaleDateString('fr-FR')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                {selectedDemarches.length > 0 && (
                  <Alert severity="success" sx={{ mt: 2, fontSize: '0.875rem' }}>
                    {selectedDemarches.length} démarche{selectedDemarches.length > 1 ? 's' : ''} sélectionnée{selectedDemarches.length > 1 ? 's' : ''}
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || importing}>
          Annuler
        </Button>

        {!syncData ? (
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={loading || !username || !password}
            startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
          >
            {loading ? 'Synchronisation...' : 'Synchroniser'}
          </Button>
        ) : syncData.new > 0 ? (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || selectedDemarches.length === 0}
            startIcon={importing ? <CircularProgress size={20} /> : <CheckIcon />}
            color="success"
          >
            {importing ? 'Import en cours...' : `Importer (${selectedDemarches.length})`}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default AnefSyncModal;
